'use client';

import { useState, FormEvent } from 'react';
import Image from 'next/image';
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/providers/LanguageContext";
import RecaptchaScript, { executeRecaptcha } from "@/components/RecaptchaScript";
import { API } from "@/lib/api";
import { toaster } from "@/components/ui/toaster";
import styles from "../app/page.module.css";
import { log } from "@/lib/log";

export function ContactSection() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check if email is valid
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  // Check if message is valid (at least 3 words)
  const isValidMessage = (message: string) => {
    const words = message.split(' ').filter(word => word.trim() !== '');
    return words.length >= 3;
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setErrorMessage(null);
    setSubmitSuccess(false);
    
    // Validate inputs
    if (!isValidEmail(email)) {
      setErrorMessage(language === 'es' ? 'Dirección de correo electrónico inválida.' : 'Invalid email address.');
      return;
    }
    
    if (!isValidMessage(message)) {
      setErrorMessage(language === 'es' ? 'El mensaje debe tener al menos 3 palabras.' : 'Message must be at least 3 words.');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Get reCAPTCHA token
      const token = await executeRecaptcha();
      
      // Submit form
      await API.contact.submit(email, message, token, undefined, 'HomePage');
      
      // Handle success
      setSubmitSuccess(true);
      setEmail('');
      setMessage('');
      
      // Show success message
      toaster.create({
        title: language === 'es' ? '¡Enviado!' : 'Submitted!',
        description: language === 'es' 
          ? 'Un miembro del equipo se pondrá en contacto contigo pronto.' 
          : 'A team member will be in contact with you shortly.',
        type: 'success'
      });
      
    } catch (error: unknown) {
      log.error('Contact form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : (
        language === 'es' 
          ? 'Hubo un problema al enviar tu mensaje. Por favor intenta de nuevo.' 
          : 'There was a problem submitting your message. Please try again.'
      );
      setErrorMessage(errorMessage);
      
      toaster.create({
        title: language === 'es' ? 'Error' : 'Error',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="contact" className={styles['contact-section']}>
      {/* reCAPTCHA is only needed for this public contact form, so load it here
          rather than globally (it otherwise floats a badge over the dashboard). */}
      <RecaptchaScript />
      <div className={styles['contact-shell']}>
        <h2 className={`${styles['features-title']} ${styles['sectionTitleCentered']}`}>
          {language === "es" ? "Contacto" : "Contact"}
        </h2>
        <div className={styles['contact-container']}>
          <div className={styles['contact-info']}>
            <p className={styles['contact-text']}>
              {language === "es"
                ? "Ya sea que estés buscando clases de inglés para tu hijo o estés interesado en mejorar tus propias habilidades lingüísticas, podemos encontrar una manera de ayudarte a alcanzar tus metas. ¡No dudes en contactarnos para más información!"
                : "Whether you're looking for English classes for your child or interested in improving your own language skills, we can find a way to help you reach your goals. Don't hesitate to contact us for more information!"}
            </p>
            <p className={styles['contact-linkRow']}>
              <a href="mailto:info@ali-institute.online" className={styles['contact-link']}>
                info@ali-institute.online
              </a>
            </p>
            <p className={styles['contact-linkRow']}>
              <a href="tel:+51977108040" className={styles['contact-link']}>
                Tel: +51 977-108-040
              </a>
            </p>
            <p className={styles['contact-linkRow']}>
              <a href="tel:+51954711201" className={styles['contact-link']}>
                Tel: +51 954-711-201
              </a>
            </p>
            <p className={styles['contact-label']}>
              {language === "es" ? "Dirección:" : "Address:"}
            </p>
            <address className={styles['contact-address']}>
              Applied Language Institute EIRL<br />
              Jiron Enrique Bottger n387<br />
              Tercer Piso<br />
              Oxapampa, Pasco, Peru<br />
              RUC: 20610327967
            </address>
            <div className={styles['facebook-section']}>
              <h4 className={styles['facebook-text']}>
                {language === "es"
                  ? "¡Síguenos en Facebook!"
                  : "Follow us on Facebook!"}
              </h4>
              <a
                href="https://www.facebook.com/profile.php?id=100070930226357"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/images/fb_icon.webp"
                  alt="Facebook Link"
                  width={60}
                  height={60}
                  className={styles['facebook-icon']}
                />
              </a>
            </div>
          </div>

          <div className={styles['contact-form']}>
            <form onSubmit={handleSubmit}>
              <div className={styles['contactField']}>
                <label htmlFor="contact-email" className={styles['contactFieldLabel']}>
                  {language === "es" ? "Correo Electrónico" : "Email"}
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="contact-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${styles['contactInput']} ${errorMessage && !isValidEmail(email) ? styles['contactFieldError'] : ''}`}
                  placeholder={
                    language === "es"
                      ? "correo@ejemplo.com"
                      : "email@example.com"
                  }
                  required
                />
              </div>
              <div className={styles['contactField']}>
                <label htmlFor="contact-message" className={styles['contactFieldLabel']}>
                  {language === "es" ? "Mensaje" : "Message"}
                </label>
                <textarea
                  id="contact-message"
                  name="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className={`${styles['contactTextarea']} ${errorMessage && !isValidMessage(message) ? styles['contactFieldError'] : ''}`}
                  required
                ></textarea>
              </div>
              
              {errorMessage && (
                <div className={styles['contactFeedbackError']}>
                  {errorMessage}
                </div>
              )}
              
              {submitSuccess && (
                <div className={styles['contactFeedbackSuccess']}>
                  {language === "es" 
                    ? "¡Mensaje enviado! Un miembro del equipo se pondrá en contacto contigo pronto." 
                    : "Message sent! A team member will be in contact with you shortly."}
                </div>
              )}
              
              <Button
                colorScheme="purple"
                type="submit"
                disabled={submitting}
              >
                {submitting 
                  ? (language === "es" ? "Enviando..." : "Submitting...")
                  : (language === "es" ? "Enviar" : "Submit")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
